import { AlertMessage } from '@/app/components'
import { Grid, Text } from '@/components'
import { useAppDispatch, useAppSelector } from '@/hooks'
import { thermostatComfortSettingsCollection } from '@/reducers/thermostatComfortSettings'
import { Thermostat, ThermostatComfortSetting } from '@/types'
import { debounce } from '@/utils/limit'
import { useCallback, useMemo, useState } from 'react'
import EventSetpointStepper from '../EventSetpointStepper'

// Largest relative shift (°F) we let a setpoint move during an event, matching
// the bounds the previous slider exposed.
const MAX_EVENT_SETPOINT_DELTA = 15

type Props = {
  comfortSettings: ThermostatComfortSetting[] // Now expecting a single comfortSetting object
  thermostatId: Thermostat['id']
  onChange?: (comfortSettings: ThermostatComfortSetting[]) => void // Adjusted to expect a single comfortSetting object
  // Whether to render the built-in instructional copy above the steppers. Off
  // when the host page (e.g. Event Temperatures) renders its own intro.
  showInstructions?: boolean
}

export default function SettingDeltaSlideEditor(props: Props) {
  const dispatch = useAppDispatch()
  const comfortSettingsUpdateError = useAppSelector((state) =>
    thermostatComfortSettingsCollection.selectors.queryState
      .select(state, {
        method: 'PATCH',
      })
      .some((queryState) => Boolean(queryState.error)),
  )
  // used to remount the steppers if updating failed -> resets to last valid value
  const [resetKey, setResetKey] = useState(false)

  // Nest thermostats have no schedule, so a single relative delta is applied to
  // every comfort setting via bulk_update.
  const firstComfortSetting = props.comfortSettings[0]
  const defaultDelta = firstComfortSetting?.event_default_setpoint_delta ?? 1
  // Heating shifts down during an event -> shown as a negative value.
  const heatInitialValue = -(
    firstComfortSetting?.event_heat_setpoint_delta ?? defaultDelta
  )
  // Cooling shifts up during an event -> shown as a positive value.
  const coolInitialValue =
    firstComfortSetting?.event_cool_setpoint_delta ?? defaultDelta

  const performUpdate = useCallback(
    async (updatedSetting: Partial<ThermostatComfortSetting>) => {
      const promise = await dispatch(
        thermostatComfortSettingsCollection.actions.updateMany(
          `/thermostats/${props.thermostatId}/comfort_settings/bulk_update/`,
          props.comfortSettings.map((s) => s.id),
          updatedSetting,
        ),
      )
      if (promise.type.includes('UPDATE_FAILURE')) {
        setResetKey((prev) => !prev)
      }
      props.onChange?.(props.comfortSettings)
    },
    [dispatch, props.thermostatId, props.comfortSettings, props.onChange],
  )

  // Separate debounced dispatchers so a heat edit immediately followed by a cool
  // edit (or vice versa) doesn't clobber the first update.
  const debouncedHeatUpdate = useMemo(
    () => debounce(performUpdate, 300),
    [performUpdate],
  )
  const debouncedCoolUpdate = useMemo(
    () => debounce(performUpdate, 300),
    [performUpdate],
  )

  const onHeatChange = (value: number) => {
    // NumberInput fires onChange with the initial value on mount; ignore no-ops.
    if (value === heatInitialValue) return
    debouncedHeatUpdate({ event_heat_setpoint_delta: Math.abs(value) })
  }

  const onCoolChange = (value: number) => {
    if (value === coolInitialValue) return
    debouncedCoolUpdate({ event_cool_setpoint_delta: value })
  }

  if (!props.comfortSettings.length) {
    return (
      <Grid flow="row" gap="32px">
        <AlertMessage variant="error" show>
          There was an issue fetching your comfort settings. Please try again
          later.
        </AlertMessage>
      </Grid>
    )
  }

  return (
    <Grid flow="row" gap="24px">
      <AlertMessage variant="error" show={Boolean(comfortSettingsUpdateError)}>
        There was an issue updating your comfort setting. Please try again
        later.
      </AlertMessage>
      {(props.showInstructions ?? true) && (
        <Text variant="body2">
          We'll shift your temperature from what your Nest is set at when the
          event starts. For example, +3 for a cooling event means we'll shift
          your temperature from 72° to 75° when the event starts.
        </Text>
      )}
      <EventSetpointStepper
        key={`heat-${Number(resetKey)}`}
        variant="heat"
        title="Heating"
        initialValue={heatInitialValue}
        range={[-MAX_EVENT_SETPOINT_DELTA, -1]}
        onChange={onHeatChange}
      />
      <EventSetpointStepper
        key={`cool-${Number(resetKey)}`}
        variant="cool"
        title="Cooling"
        initialValue={coolInitialValue}
        range={[1, MAX_EVENT_SETPOINT_DELTA]}
        showPlusSign
        onChange={onCoolChange}
      />
    </Grid>
  )
}
